import React, { useContext, useEffect, useState, useReducer } from "react";
import { MapIcon } from "@heroicons/react/24/outline";
import { FormattedMessage, useIntl } from "react-intl";
import { PlusIcon, CheckIcon, TrashIcon } from "@heroicons/react/24/solid";

import RadioGroup from "@ui-components/RadioGroup";
import Modal from "@ui-components/Modal";
import Select from "@ui-components/Select";
import Button from "@ui-components/Button";
import Input from "@ui-components/Input";
import AuthenticationService from "@services/AuthenticationService";
import TaskMapEditor from "./TaskMapEditor";
import useCountryAxiosInstance from "@hooks/useCountryAxiosInstance";
import CountryContext from "../../../CountryContext";
import ImageUploader from "../ImageUploader";
import useImageUploader from "./hooks/useImageUploader";

const NEW_TASKMAP_NE_OPTIONS = ["new", "existing"];
const NEW_TASKMAP_TYPE_OPTIONS = ["photos", "planimetries"];

const selectedInitialDefaultState = {
  new_existing: "existing",
  type: "photos",
  is_editing_image: false,
  selected_map: null,
  map_name: "",
  canvas_data: null,
};

function selectedStateReducer(state, action) {
  switch (action.type) {
    case "set_new_existing":
      return {
        ...selectedInitialDefaultState,
        new_existing: action.value,
      };
    case "set_type":
      return {
        ...selectedInitialDefaultState,
        new_existing: state.new_existing,
        is_editing_image: state.is_editing_image,
        type: action.value,
      };
    case "set_map_name":
      return {
        ...state,
        map_name: action.value,
      };
    case "set_selected_map":
      return {
        ...state,
        selected_map: action.value,
        new_existing: action.new_existing || state.new_existing,
        type: action.value_type || state.type,
        map_name: action.map_name || state.map_name,
      };
    case "set_is_editing_image":
      return {
        ...state,
        is_editing_image: action.value,
      };
    case "set_canvas_data":
      return {
        ...state,
        canvas_data: action.value,
      };
    default:
      throw new Error();
  }
}

const TaskMapModal = ({
  workditeId,
  taskMapId,
  editMode,
  onExit,
  onSave,
  taskId = null,
  initialState = null,
  showControls = null,
}) => {
  // hooks
  const intl = useIntl();
  const countryApiRequest = useCountryAxiosInstance();
  const { country } = useContext(CountryContext);
  const userId = AuthenticationService.getUserData().id;
  // state
  const [maps, setMaps] = useState(null);

  const [selectedState, dispatchSelectedState] = useReducer(
    selectedStateReducer,
    initialState || selectedInitialDefaultState
  );

  const [showDeleteMapDialog, setShowDeleteMapDialog] = useState(false);
  const controlsVisible = (showControls !== null ? showControls : !taskMapId);

  // eslint-disable-next-line
  const {
    uploadingImage,
    saveCurrentMapName,
    setUploadingImage,
    onImageUploaded,
    deleteMap,
  } = useImageUploader(workditeId, selectedState.type);

  useEffect(() => {
    countryApiRequest
      .get(`/worksites_tasks_maps/all`, {
        params: { id_worksite: workditeId, join: "image" },
      })
      .then(setMaps);
  }, [taskId, countryApiRequest]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!taskMapId || !maps) return;
    const target = maps.find((m) => m.id === taskMapId);
    if (!target) return;
    dispatchSelectedState({
      type: "set_selected_map",
      value: target,
      value_type: target.des_type,
      new_existing: "existing",
      map_name: target.image.val_description,
    });
  }, [taskId, taskMapId, maps, countryApiRequest]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Modal
        onExit={onExit}
        closeClickOutside={false}
        large
        className="align-middle w-full"
      >
        <h1 className="flex items-center gap-3 text-2xl font-extrabold mb-4">
          <MapIcon className="w-7" />
          <FormattedMessage id="task_map" />
        </h1>
        {editMode && controlsVisible && (
          <div className="my-4 flex flex-col gap-3">
            <RadioGroup
              id="task-map-type-rg"
              options={NEW_TASKMAP_NE_OPTIONS.map((o) => ({
                name: intl.formatMessage({ id: `${o}_f` }),
                value: o,
              }))}
              currentValue={selectedState.new_existing}
              onChange={(e) => {
                dispatchSelectedState({
                  type: "set_new_existing",
                  value: e,
                });
              }}
              horizontal
            />
            <Select
              label={intl.formatMessage({ id: "taskmap_material" })}
              options={NEW_TASKMAP_TYPE_OPTIONS.map((o) => ({
                name: intl.formatMessage({ id: o }),
                value: o,
              }))}
              value={selectedState.type}
              onChange={(e) => {
                dispatchSelectedState({
                  type: "set_type",
                  value: e.target.value,
                });
              }}
            />
            {!!maps &&
              selectedState.new_existing === "existing" &&
              selectedState.type && (
                <Select
                  label={intl.formatMessage({ id: "select_option" })}
                  options={[{ name: "", value: undefined }].concat(
                    maps
                      .filter((o) => {
                        return selectedState.type
                          ? selectedState.type === o.des_type
                          : true;
                      })
                      .map((o) => ({
                        name: o.image.val_description,
                        value: o.id,
                      }))
                  )}
                  value={selectedState.selected_map?.id}
                  onChange={(e) => {
                    const target = maps.find(
                      ({ id }) => parseInt(id) === parseInt(e.target.value)
                    );
                    dispatchSelectedState({
                      type: "set_selected_map",
                      value: target,
                    });
                  }}
                />
              )}
            {selectedState.new_existing === "new" &&
              !selectedState.selected_map && (
                <>
                  <ImageUploader
                    prefix={`images/${country.code}/tasks/maps/${userId}`}
                    bucket="task_images"
                    uploading={uploadingImage}
                    setUploading={setUploadingImage}
                    setIsEditingImage={(value) => {
                      dispatchSelectedState({
                        type: "set_is_editing_image",
                        value,
                      });
                    }}
                    onUploadComplete={async (url) => {
                      const current = await onImageUploaded(url);
                      setMaps((prev) => [current, ...prev]);
                      dispatchSelectedState({
                        type: "set_selected_map",
                        value: current,
                        map_name: current.image.val_description,
                      });
                    }}
                    accept={
                      selectedState.type === "planimetries"
                        ? "application/pdf"
                        : "image/*"
                    }
                    allowEdit={true}
                  >
                    {!selectedState.is_editing_image && (
                      <Button submitting={uploadingImage}>
                        <PlusIcon className="-ml-0.5 w-4" />
                        <FormattedMessage id="add" />
                      </Button>
                    )}
                  </ImageUploader>
                </>
              )}
            {selectedState.new_existing === "new" &&
              !!selectedState.selected_map && (
                <>
                  <div className="flex gap-3 items-end">
                    <div className="flex-1 font-semibold">
                      <Input
                        label={
                          intl.formatMessage({ id: "name" }) +
                          (selectedState.map_name !==
                          selectedState.selected_map.image.val_description
                            ? ` (${intl.formatMessage({ id: "edited" })})`
                            : "")
                        }
                        value={selectedState.map_name}
                        onChange={(e) => {
                          dispatchSelectedState({
                            type: "set_map_name",
                            value: e.target.value,
                          });
                        }}
                        hideBottomMargin
                      />
                    </div>
                    <div className="flex gap-2 mb-1.5">
                      {selectedState.map_name !==
                        selectedState.selected_map.image.val_description && (
                        <Button
                          styleType="default"
                          disabled={!selectedState.map_name}
                          onClick={async () => {
                            const updated = await saveCurrentMapName(
                              selectedState.selected_map.image,
                              selectedState.map_name
                            );
                            setMaps((prev) =>
                              prev.map((m) =>
                                m.id === selectedState.selected_map.id
                                  ? { ...m, image: updated }
                                  : m
                              )
                            );
                            dispatchSelectedState({
                              type: "set_selected_map",
                              value: {
                                ...selectedState.selected_map,
                                image: updated,
                              },
                            });
                          }}
                        >
                          <CheckIcon className="w-4" />
                          <FormattedMessage id="save" />
                        </Button>
                      )}
                      <Button
                        styleType="danger"
                        onClick={() => {
                          setShowDeleteMapDialog(true);
                        }}
                      >
                        <TrashIcon className="w-4" />
                        <FormattedMessage id="delete" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
          </div>
        )}
        {!controlsVisible && selectedState.map_name && (
          <div className="font-lg font-bold my-5">
            {selectedState.map_name} ({intl.formatMessage({ id: selectedState.type })})
          </div>
        )}
        {selectedState.selected_map && (
          <div style={{ height: "36rem" }}>
            <TaskMapEditor
              url={selectedState.selected_map.image.val_url}
              editMode={editMode}
              taskId={taskId}
              taskMapId={taskMapId}
              onSave={(canvas_data) => {
                onSave({
                  ...selectedState,
                  canvas_data,
                  taskMapId: taskMapId || selectedState.selected_map.id,
                });
                dispatchSelectedState({
                  type: "set_canvas_data",
                  value: canvas_data,
                });
              }}
              initialState={selectedState?.canvas_data}
            />
          </div>
        )}
      </Modal>
      {showDeleteMapDialog && !!selectedState.selected_map && (
        <Modal
          title={<FormattedMessage id="confirm_delete" />}
          description={
            <FormattedMessage
              id="confirm_delete_worksite_task_map"
              values={{
                name: (
                  <span className="font-semibold">
                    {selectedState.selected_map.image.val_description}
                  </span>
                ),
              }}
            />
          }
          onConfirm={async () => {
            await deleteMap(selectedState.selected_map);
            setMaps((prev) =>
              prev.filter(({ id }) => id !== selectedState.selected_map.id)
            );
            setShowDeleteMapDialog(false);
            dispatchSelectedState({
              type: "set_selected_map",
              value: null,
            });
          }}
          onDismiss={() => {
            setShowDeleteMapDialog(false);
          }}
          onExit={() => {
            setShowDeleteMapDialog(false);
          }}
        />
      )}
    </>
  );
};

export default TaskMapModal;
